import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Swords, Plus, ArrowLeft, MapPin, Users, ListOrdered, Trash2, Loader2 } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/tournament";
import { CW_STATUS_META } from "@/lib/clubwars";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";

export default function ClubWars() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canUse = hasCapability(user, "cw_event") && user?.account_type !== "player";
  const [events, setEvents] = useState(null);
  const [delId, setDelId] = useState(null);

  const load = () => api.get("/club-wars").then(({ data }) => setEvents(data || [])).catch(() => setEvents([]));
  useEffect(() => { if (canUse) load(); else setEvents([]); }, [canUse]);

  const doDelete = async () => {
    try { await api.delete(`/club-wars/${delId}`); toast.success("Club War deleted"); setDelId(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not delete"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Club Wars" />
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-10 grain">
        <button data-testid="cw-back-dashboard" onClick={() => nav("/dashboard")} className="text-sm flex items-center gap-2 mb-6" style={{ color: "var(--text-2)" }}>
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Module 3 · Team vs Team</span>
            <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-2 flex items-center gap-3"><Swords className="w-8 h-8 text-primary-c" /> Club Wars</h1>
            <p className="mt-2 text-sm max-w-xl" style={{ color: "var(--text-2)" }}>Structured club-vs-club dual meets — define teams, line-ups and scoring, then run the series across your courts.</p>
          </div>
          {canUse && (
            <button data-testid="cw-new-button" onClick={() => nav("/club-wars/new")} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Club War
            </button>
          )}
        </motion.div>

        {!canUse && (
          <div data-testid="cw-locked" className="rounded-2xl glow-border p-8 text-center" style={{ background: "var(--surface)" }}>
            <Swords className="w-10 h-10 text-primary-c mx-auto mb-3" />
            <h3 className="font-display font-bold text-xl">Club Wars isn't on your plan yet</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Upgrade your subscription to unlock team-vs-team dual meets.</p>
            <button onClick={() => nav("/subscribe")} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold mt-5">View plans</button>
          </div>
        )}

        {canUse && events === null && <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>}

        {canUse && events !== null && events.length === 0 && (
          <div data-testid="cw-empty" className="rounded-2xl glow-border p-10 text-center" style={{ background: "var(--surface)" }}>
            <Swords className="w-10 h-10 text-primary-c mx-auto mb-3 opacity-70" />
            <h3 className="font-display font-bold text-lg">No Club Wars yet</h3>
            <p className="mt-1 text-sm" style={{ color: "var(--text-2)" }}>Create your first dual meet to pit two or more clubs against each other.</p>
          </div>
        )}

        {canUse && events && events.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="cw-list">
            {events.map((ev) => {
              const st = CW_STATUS_META[ev.status] || CW_STATUS_META.draft;
              return (
                <div key={ev.cw_id} data-testid={`cw-card-${ev.cw_id}`} className="rounded-2xl glow-border p-5 flex flex-col" style={{ background: "var(--surface)" }}>
                  <div className="flex items-start justify-between gap-2">
                    <button onClick={() => nav(`/club-wars/${ev.cw_id}`)} className="text-left flex-1">
                      <div className="font-display font-bold text-lg truncate">{ev.name}</div>
                    </button>
                    <span className="text-[10px] mono uppercase tracking-wider rounded-full px-2 py-0.5 shrink-0" style={{ background: `${st.color}22`, color: st.color }}>{st.l}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs" style={{ color: "var(--text-2)" }}>
                    {ev.venue && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{ev.venue}</span>}
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{(ev.teams || []).length} teams</span>
                    <span className="flex items-center gap-1"><ListOrdered className="w-3.5 h-3.5" />{(ev.lines || []).length} lines</span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button data-testid={`cw-open-${ev.cw_id}`} onClick={() => nav(`/club-wars/${ev.cw_id}`)} className="flex-1 btn-glow rounded-full py-2 text-sm font-bold">Open & edit</button>
                    <button data-testid={`cw-delete-${ev.cw_id}`} onClick={() => setDelId(ev.cw_id)} aria-label="Delete" className="glass rounded-full w-10 flex items-center justify-center hover:scale-105 transition-transform"><Trash2 className="w-4 h-4 text-red-400" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="cw-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this Club War?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--text-2)" }}>This permanently removes the event, its teams and line configuration.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cw-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="cw-delete-confirm" onClick={(e) => { e.preventDefault(); doDelete(); }} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
